import { useMemo, useState } from "react";
import { type Path, useFormContext, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import type { BidPostData } from "validations/bidValidation";

import { useDebounce } from "@krea/shared/utils/use-debounce";

import { useSelectedBidIndex } from "@/components/lender-application-details/bids/SelectedBidIndexContext";
import {
  BidTypeEnum,
  OfferAttractivenessRecommendationStatusEnum,
} from "@/features/common/enums";
import { useGetOfferAttractiveness } from "@/features/lender-responses/hooks/data/use-get-offer-attractiveness.query";

import { InterestRateExplanationModal } from "./interest-rate-explanation-modal";
import { OfferAttractivenessCard } from "./offer-attractiveness-card";
import { OfferAttractivenessLocked } from "./offer-attractiveness-locked";

function getFieldNameForRecommendation(
  status: OfferAttractivenessRecommendationStatusEnum,
  bidType: BidTypeEnum | undefined,
  selectedBidIndex: number,
): Path<BidPostData> | null {
  switch (status) {
    case OfferAttractivenessRecommendationStatusEnum.LOWER_COMPARABLE_INTEREST_RATE:
      return null;
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_AMOUNT:
      return bidType === BidTypeEnum.FACTORING
        ? (`bids.${selectedBidIndex}.purchaseLimit` as Path<BidPostData>)
        : (`bids.${selectedBidIndex}.amount` as Path<BidPostData>);
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_REPAYMENT_TIME:
      if (bidType === BidTypeEnum.REVOLVING_CREDIT) {
        return `bids.${selectedBidIndex}.monthlyAmortisation` as Path<BidPostData>;
      }

      return `bids.${selectedBidIndex}.paybackTimeMonths` as Path<BidPostData>;
    default:
      return null;
  }
}

interface OfferAttractivenessProps {
  lenderApplicationId: number;
  isFeatureDisabled?: boolean;
}

const OfferAttractiveness = ({
  lenderApplicationId,
  isFeatureDisabled = false,
}: OfferAttractivenessProps) => {
  const [interestRateModalOpen, setInterestRateModalOpen] = useState(false);

  const {
    formState: { isValid, isDirty },
    control,
    setFocus,
  } = useFormContext<BidPostData>();

  const selectedBidIndex = useSelectedBidIndex();
  const bids = useWatch({ control, name: "bids" });
  const bidType = bids[selectedBidIndex]?.type as BidTypeEnum | undefined;

  const handleRecommendationClick = (
    status: OfferAttractivenessRecommendationStatusEnum,
  ) => {
    if (
      status ===
      OfferAttractivenessRecommendationStatusEnum.LOWER_COMPARABLE_INTEREST_RATE
    ) {
      setInterestRateModalOpen(true);

      return;
    }

    const fieldName = getFieldNameForRecommendation(
      status,
      bidType,
      selectedBidIndex,
    );

    if (fieldName) {
      setFocus(fieldName);
    }
  };

  const debouncedData = useDebounce(
    useMemo(
      () => ({ bidData: { bids }, isDirty, isValid }),
      [bids, isDirty, isValid],
    ),
    2000,
  );

  const offerAttractivenessFormData = useMemo((): BidPostData => {
    return {
      bids: debouncedData.bidData.bids,
      fieldRequests: [],
    };
  }, [debouncedData]);

  const { lenderCompanyId } = useParams<{ lenderCompanyId: string }>();
  const isQueryEnabled =
    !isFeatureDisabled &&
    offerAttractivenessFormData.bids.length > 0 &&
    debouncedData.isValid &&
    debouncedData.isDirty;

  const { data } = useGetOfferAttractiveness(
    {
      lenderCompanyId: Number(lenderCompanyId),
      lenderApplicationId,
      input: offerAttractivenessFormData,
    },
    { enabled: isQueryEnabled },
  );

  const card = (
    <OfferAttractivenessCard
      score={isFeatureDisabled ? null : (data?.score ?? null)}
      recommendations={isFeatureDisabled ? [] : (data?.recommendations ?? [])}
      bidType={bidType}
      onRecommendationClick={handleRecommendationClick}
    />
  );

  if (isFeatureDisabled) {
    return <OfferAttractivenessLocked />;
  }

  return (
    <>
      {card}
      <InterestRateExplanationModal
        open={interestRateModalOpen}
        setOpen={setInterestRateModalOpen}
      />
    </>
  );
};

export { OfferAttractiveness };
